import { AxiosRequestConfig } from 'axios';
import { oneLine } from 'common-tags';
import { get, intersection, merge } from 'lodash';
import { normalize, sep as pathSep } from 'path';
import { URL } from 'url';

import { REG_FULL_TRAILING_SLASH } from '../../regex/regex';
import { RequestConfig } from '../types';
import { AWSCredentials } from './aws-credentials';
import { AWS4SignerConfig, AWSRequestConfigSet } from './types';

export class AWSRequestConfig implements RequestConfig {

  private neededKeys: string[] = ['host', 'path', 'service', 'region'];
  private requestConfig: AWSRequestConfigSet;

  constructor(
    private axiosRequestConfig: AxiosRequestConfig,
    private aws4SignerConfig: AWS4SignerConfig,
    private credentials: AWSCredentials
  ) {
    this.requestConfig = merge({}, axiosRequestConfig, aws4SignerConfig);
    this.sanitize();
  }

  public getConfig(): AWSRequestConfigSet {
    return this.requestConfig;
  }

  public updateConfig(config: Partial<AWSRequestConfigSet>): AWSRequestConfig {
    this.requestConfig = merge({}, this.requestConfig, config);

    return this;
  }

  public validate(): boolean | never {
    const configKeys = Object.keys(this.requestConfig);

    if (intersection(this.neededKeys, configKeys).length !== this.neededKeys.length) {
      throw new Error(
        oneLine`Some of the required keys on the AWS4 sign proccess are missed.
        Required keys: '${this.neededKeys.toString()}'`
      );
    }

    return true;
  }

  /**
   * Sanitizes the configuration in order to clean and add missed params.
   */
  public sanitize(): void {
    const config = { ...this.requestConfig };

    if (!config.hasOwnProperty('body') && config.hasOwnProperty('data')) {
      config.body = JSON.stringify(config.data); // aws4 looks for body; axios for data
    }

    const url = get(config, 'url');

    if (url && url !== get(config, 'path')) {
      config.path = config.url;
    }

    if (!config.hasOwnProperty('host') && config.hasOwnProperty('baseURL')) {
      config.host = new URL(config.baseURL!).hostname;
    }

    const { host, path, baseURL } = config;

    const sanitized = {
      ...config,
      host: this.removeFullTralingSlash(host),
      baseURL: this.removeFullTralingSlash(baseURL),
      path: this.sanitizeUrl(path),
      url: this.sanitizeUrl(url)
    };

    this.updateConfig(sanitized);
  }

  /**
   * Cleans the header of the request.
   * @param {requestConfig} requestConfig
   * @returns cleaned requestConfig
   */
  public cleanHeaders = (): AWSRequestConfig => {
    delete this.requestConfig.headers?.Host;
    delete this.requestConfig.headers?.host;

    return this;
  }

  /**
   * Adds to the neccesary tokens to the headers config
   * @param {AWSCredentials} credentials
   */
  public addJWTAuthorizationHeader(credentials: AWSCredentials): void {
    this.updateConfig({
      headers: {
        Authorization: credentials.jwtToken
      }
    });
  }

  /**
   * Sanitizes the urls.
   * @param {string} url
   * @returns {string} sanitize url
   */
  private sanitizeUrl(url = ''): string {
    const tmpUrl = this.containsQueryParams(url) ? `${pathSep}${url}` : `${pathSep}${url}${pathSep}`;

    return normalize(tmpUrl);
  }

  /**
   * Removes all the trailing slashed around an url.
   * @param {string} url
   * @returns url without trailing slashes
   */
  private removeFullTralingSlash(url = ''): string {
    return url.replace(REG_FULL_TRAILING_SLASH, '');
  }

  /**
   * Checks if an url contains queryParams
   * @param {string} url
   * @returns {boolean}
   */
  private containsQueryParams(url = ''): boolean {
    return url.includes('/?');
  }
}
